import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Typography,
  Box
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { Payment, PaymentFormData } from '../../types/Payment';
import { RegistrationConfirmation } from '../../types/RegistrationConfirmation';
import { paymentService } from '../../services/paymentService';

import { handleApiError } from '../../utils/apiErrorHandler';

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  registrationConfirmation: RegistrationConfirmation;
  onPaymentUpdated: (payment: Payment) => void;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({
  open,
  onClose,
  registrationConfirmation,
  onPaymentUpdated
}) => {
  const expectedAmount = registrationConfirmation.fees.totalAmount;

  const [formData, setFormData] = useState<PaymentFormData>({
    amount: 0,
    receivedDate: dayjs().format('YYYY-MM-DD'),
    notes: ''
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [existingPayment, setExistingPayment] = useState<Payment | null>(null);

  // Lade bestehende Zahlungsdaten, falls vorhanden
  useEffect(() => {
    const fetchPayment = async () => {
      if (registrationConfirmation._id && open) {
        try {
          const payment = await paymentService.getPaymentByConfirmation(registrationConfirmation._id);
          if (payment) {
            setExistingPayment(payment);
            setFormData({
              amount: payment.amount,
              receivedDate: dayjs(payment.receivedDate).format('YYYY-MM-DD'),
              notes: payment.notes || ''
            });
          } else {
            // Falls keine Zahlung existiert, Standardwerte setzen
            setExistingPayment(null);
            setFormData({
              amount: 0,
              receivedDate: dayjs().format('YYYY-MM-DD'),
              notes: ''
            });
          }
        } catch (error) {
          handleApiError(error, 'Fehler beim Laden der Zahlungsdaten.');
        }
      }
    };

    fetchPayment();
  }, [registrationConfirmation, open]);

  // Handler für Textfelder
  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = e.target.name;
    const value = e.target.value;
    
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' ? (value === '' ? 0 : parseFloat(value)) : value
    }));
  };

  // Handler für DatePicker
  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setFormData(prev => ({
        ...prev,
        receivedDate: date.format('YYYY-MM-DD')
      }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payment = await paymentService.updatePayment(registrationConfirmation._id, formData);
      onPaymentUpdated(payment);
      onClose();
    } catch (error) {
      handleApiError(error, 'Fehler beim Speichern der Zahlung.');
    } finally {
      setLoading(false);
    }
  };

  // Berechne Differenz zwischen erwartetem und tatsächlichem Betrag
  const difference = expectedAmount - formData.amount;
  const isFullyPaid = formData.amount >= expectedAmount;
  const isPartiallyPaid = formData.amount > 0 && !isFullyPaid;
  const showStatusPreview = formData.amount > 0 || expectedAmount === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {existingPayment ? 'Zahlung bearbeiten' : 'Zahlung erfassen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Wettbewerb und Verein anzeigen */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Wettbewerb:
            </Typography>
            <Typography variant="body1">
              {typeof registrationConfirmation.competition === 'object' 
                ? registrationConfirmation.competition.name 
                : 'Unbekannter Wettbewerb'}
            </Typography>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Verein:
            </Typography>
            <Typography variant="body1">
              {typeof registrationConfirmation.club === 'object' 
                ? registrationConfirmation.club.name 
                : 'Unbekannter Verein'}
            </Typography>
            
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Erwarteter Betrag:
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {expectedAmount.toFixed(2)} €
            </Typography>
          </Box>

          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="amount">Eingegangener Betrag</InputLabel>
            <OutlinedInput
              id="amount"
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleTextFieldChange}
              startAdornment={<InputAdornment position="start">€</InputAdornment>}
              label="Eingegangener Betrag"
            />
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Eingangsdatum"
              value={dayjs(formData.receivedDate)}
              onChange={handleDateChange}
              format="DD.MM.YYYY"
            />
          </LocalizationProvider>

          <TextField
            name="notes"
            label="Anmerkungen"
            multiline
            rows={3}
            value={formData.notes}
            onChange={handleTextFieldChange}
            fullWidth
          />

          {/* Zahlungsstatus-Anzeige */}
          {showStatusPreview && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle1">Zahlungsstatus:</Typography>
              <Box sx={{
                mt: 1,
                p: 1,
                bgcolor: isFullyPaid
                  ? 'success.light'
                  : isPartiallyPaid
                    ? 'warning.light'
                    : 'error.light',
                color: isFullyPaid
                  ? 'success.contrastText'
                  : isPartiallyPaid
                    ? 'warning.contrastText'
                    : 'error.contrastText',
                borderRadius: 1,
                fontWeight: 'bold'
              }}>
                {isFullyPaid
                  ? 'Vollständig bezahlt'
                  : isPartiallyPaid
                    ? `Teilweise bezahlt (${difference.toFixed(2)}€ ausstehend)`
                    : 'Nicht bezahlt'}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Abbrechen
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Wird gespeichert...' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;